import { useEffect, useState, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import { applyLocaleToUrl, renderRichText } from "@/utils/formatters";
import arrowLeft from "@/assets/arrow-left.png";
import arrowRight from "@/assets/arrow-right.png";

const ShoppingGuideCarousel = ({ shoppingGuideCarouselData = {}, locale }) => {
  const {
    heading = "",
    description = "",
    richDescription = "",
    slides = [],
    showUpperLine = false,
    showBottomLine = false,
  } = shoppingGuideCarouselData;

  const description2 = renderRichText(richDescription);
  const iconLeft = arrowLeft?.src;
  const iconRight = arrowRight?.src;
  const [index, setIndex] = useState(0);
  const [thumbIndex, setThumbIndex] = useState(2);
  const [isAnimating, setIsAnimating] = useState(true);
  const isResettingRef = useRef(false);

  // Responsive item width
  const [ITEM_WIDTH, setItemWidth] = useState(88);

  useEffect(() => {
    // Note: Use this function only inside useEffect as window
    // object is not available on initial page load in Next.js
    const getItemWidth = () => {
      if (window.innerWidth < 640) return 64;
      if (window.innerWidth < 768) return 72;
      return 88;
    };

    const handleResize = () => {
      setItemWidth(getItemWidth());
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const extendedSlides = useMemo(
    () => [
      slides?.[slides?.length - 2],
      slides?.[slides?.length - 1],
      ...slides,
      slides?.[0],
      slides?.[1],
    ],
    [slides]
  );

  const onNextClick = () => {
    if (!isResettingRef.current) setThumbIndex(prev => prev + 1);
  };

  const onPrevClick = () => {
    if (!isResettingRef.current) setThumbIndex(prev => prev - 1);
  };

  // Auto slide
  useEffect(() => {
    const timer = setInterval(() => {
      if (!isResettingRef.current) setThumbIndex(prev => prev + 1);
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  // Infinite loop logic
  useEffect(() => {
    let timer;
    if (thumbIndex <= 1 && !isResettingRef.current) {
      isResettingRef.current = true;
      timer = setTimeout(() => {
        setIsAnimating(false);
        setThumbIndex(slides?.length + 1);
      }, 500);
    }

    if (thumbIndex >= slides?.length + 2 && !isResettingRef.current) {
      isResettingRef.current = true;
      timer = setTimeout(() => {
        setIsAnimating(false);
        setThumbIndex(2);
      }, 500);
    }
    return () => clearTimeout(timer);
  }, [thumbIndex, slides.length]);

  useEffect(() => {
    if (!isAnimating) {
      requestAnimationFrame(() => {
        setIsAnimating(true);
        isResettingRef.current = false;
      });
    }
  }, [isAnimating]);

  // Logic to make sure carousel visible image is same as center thumbnail
  useEffect(() => {
    const centerExtendedIndex = thumbIndex + 1;
    const realCenterIndex =
      (centerExtendedIndex - 2 + slides.length) % slides.length;

    setIndex(realCenterIndex);
  }, [thumbIndex, slides.length]);

  const learnMore = url => {
    window.open(applyLocaleToUrl(url, locale));
  };

  return (
    <>
      {/* Top section */}
      <div className="my-10 max-w-[980px] mx-auto">
        {showUpperLine && (
          <hr className=" max-w-[980px] mx-auto w-full border-t my-10 border-[#e8e8e8] mt-10 "></hr>
        )}
        {(heading || description || description2) && (
          <div className="w-full flex flex-col items-center text-center py-4 sm:py-16 px-4">
            {heading && (
              <h2 className="text-[16px] sm:text-[18px] font-bold tracking-wide uppercase text-black font-['helveticaRoman',Helvetica,sans-serif]">
                {heading}
              </h2>
            )}
            {description && (
              <p className="mt-4 text-[13px] sm:text-[14px] leading-5 text-[#4d4d4f]">
                {description}
              </p>
            )}
            {description2 && (
              <p className="mt-4 text-[13px] sm:text-[14px] leading-5 text-[#4d4d4f] desc2">
                {description2}
              </p>
            )}
            <div className="w-[40px] h-[1px] bg-gray-400 mt-6" />
          </div>
        )}
        {/* Carousel section */}
        {slides?.length ? (
          <div className="w-full mx-auto flex flex-col lg:flex-row items-center lg:items-start justify-center gap-10 lg:gap-20 px-4 lg:pr-20 pb-5">
            {/* Left carousel section */}
            <div className="relative flex flex-col items-center">
              {/* Left arrow */}
              <button
                type="button"
                className="absolute left-[-30px] sm:left-[-40px] lg:left-[-50px] top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 
							sm:h-12 flex items-center justify-center cursor-pointer"
                onClick={onPrevClick}
              >
                <img src={iconLeft} className="w-5 sm:w-6 lg:w-7 " />
              </button>
              {/* Main carousel img */}
              <img
                src={
                  slides?.[index]?.fields?.image?.fields?.imageUrl ||
                  slides?.[index]?.fields?.image?.fields?.asset?.fields?.file
                    ?.url
                }
                className="w-[220px] sm:w-[260px] md:w-[300px] h-auto transition-all duration-500"
                alt={slides?.[index]?.fields?.image?.fields?.altText}
              />
              {/* Right arrow */}
              <button
                type="button"
                className="absolute cursor-pointer right-[-30px] sm:right-[-40px] lg:right-[-50px] top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center"
                onClick={onNextClick}
              >
                <img src={iconRight} className="w-5 sm:w-6 lg:w-7" />
              </button>
              {/* Thumbnails section */}
              <div className="mt-8 sm:mt-10 w-[200px] sm:w-[240px] md:w-[264px] overflow-hidden">
                <div
                  className="flex gap-4 sm:gap-6"
                  style={{
                    transform: `translateX(-${thumbIndex * ITEM_WIDTH}px)`,
                    transition: isAnimating
                      ? "transform 0.6s ease-in-out"
                      : "none",
                  }}
                >
                  {extendedSlides.map((slide, i) => {
                    const isCenter = i === thumbIndex + 1;

                    return (
                      <button
                        type="button"
                        key={i}
                        onClick={() => setThumbIndex(i - 1)}
                        className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center cursor-pointer"
                      >
                        <div
                          className={`rounded-full flex items-center justify-center transition-all duration-300 ${isCenter ? "bg-white border border-[#dcdcdc] scale-110 z-10" : "bg-[#CCC] border-[4px] sm:border-[6px] border-[#CCC] opacity-70 scale-95"}`}
                        >
                          <img
                            src={
                              slide?.fields?.image?.fields?.imageUrl ||
                              slide?.fields?.image?.fields?.asset?.fields?.file
                                ?.url
                            }
                            className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full${!isCenter ? " mix-blend-multiply" : ""}`}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            {/* Right text section */}
            <div className="w-[90%] sm:w-[400px] lg:w-[300px] lg:pl-10 text-center lg:text-left">
              <p className="text-[12px] text-black font-bold mb-6 leading-[1.4]">
                {slides?.[index]?.fields?.imageDescription || ""}
              </p>
              {slides?.[index]?.fields?.cta?.fields?.label ? (
                <button
                  type="button"
                  onClick={() =>
                    learnMore(slides?.[index]?.fields?.cta?.fields?.url)
                  }
                  className="text-[10px] border-2 border-black px-[14px] py-[7px] uppercase bg-white cursor-pointer inline-block text-black font-bold hover:bg-black hover:text-white transition "
                >
                  {slides?.[index]?.fields?.cta?.fields?.label || ""}
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
        {showBottomLine && (
          <hr className=" max-w-[980px] mx-auto w-full border-t my-10 border-[#e8e8e8] mt-10 "></hr>
        )}
      </div>
    </>
  );
};

ShoppingGuideCarousel.propTypes = {
  shoppingGuideCarouselData: PropTypes.object,
  locale: PropTypes.string,
};

export default ShoppingGuideCarousel;
